import {useCallback, useEffect, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    Calculator,
    FilePlus2,
    FileSpreadsheet,
    History,
    Landmark,
    MoveRight,
    RotateCcw,
    Search,
} from "lucide-react";
import {Link} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {addMoney, formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime, formatNumber} from "../../shared/utils/format";
import {useTranslation} from "../../shared/i18n";
import {
    buildSettlementReport,
    createSettlementDraft,
    DEFAULT_SETTLEMENT_PAGE_SIZE,
    listSettlementCompanies,
    listSettlementCompanyBranches,
    listSettlements,
    settlementKeys,
} from "./api";
import {
    getReadinessPresentation,
    getSettlementStatusPresentation,
} from "./statusMachine";
import "./settlements.css";

const EMPTY_FILTERS = Object.freeze({
    status: "",
    readiness_status: "",
    company_id: "",
    branch_id: "",
    period_start: "",
    period_end: "",
});

const STATUS_OPTIONS = [
    "generated",
    "draft",
    "approved",
    "exported",
    "sent_to_bank",
    "partially_paid",
    "paid",
    "cancelled",
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const READINESS_OPTIONS = [
    "ready",
    "missing_requisites",
    "missing_documents",
    "needs_review",
    "on_hold",
];

const companySelectOptions = (companies, t) => [
    {value: "", label: t("money.settlements.allCompanies")},
    ...companies.map((company) => ({
        value: String(company.id),
        label: company.bin_number ? `${company.name} · ${company.bin_number}` : company.name,
    })),
];

const branchSelectOptions = (branches, {requiredCompany = false, t} = {}) => [
    {
        value: "",
        label: requiredCompany ? t("money.settlements.wholeCompany") : t("money.settlements.allBranches"),
    },
    ...branches.map((branch) => ({
        value: String(branch.id),
        label: branch.name,
    })),
];

const buildCommandFields = ({kind, companies, branches, t}) => {
    const companyField = {
        name: "company_id",
        label: t("money.settlements.company"),
        type: "select",
        required: kind === "draft",
        clears: ["branch_id"],
        options:
            kind === "draft"
                ? [
                      {value: "", label: t("money.settlements.chooseCompany")},
                      ...companySelectOptions(companies, t).slice(1),
                  ]
                : companySelectOptions(companies, t),
    };
    const branchField = {
        name: "branch_id",
        label: t("money.settlements.branch"),
        type: "select",
        options: branchSelectOptions(branches, {requiredCompany: kind === "draft", t}),
    };
    const periodFields = [
        {name: "period_start", label: t("money.settlements.periodFrom"), type: "date", required: true},
        {name: "period_end", label: t("money.settlements.periodTo"), type: "date", required: true},
    ];
    return kind === "draft"
        ? [companyField, branchField, ...periodFields]
        : [...periodFields, companyField, branchField];
};

const toCalendarDate = (value) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

/** Current local Mon–Sun week for list filters (Dilshat: open on last calendar week). */
export const currentCalendarWeekFilters = (now = new Date()) => {
    const day = now.getDay();
    const daysFromMonday = day === 0 ? 6 : day - 1;
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
    return {
        ...EMPTY_FILTERS,
        period_start: toCalendarDate(monday),
        period_end: toCalendarDate(sunday),
    };
};

const freshFilters = () => currentCalendarWeekFilters();

const currencyCode = (statement) => statement?.snapshot?.currency?.code || "";

const pageCurrency = (rows) => {
    const currencies = rows.map(currencyCode);
    if (!currencies.length || currencies.some((currency) => !currency)) return null;
    const uniqueCurrencies = new Set(currencies);
    return uniqueCurrencies.size === 1 ? currencies[0] : null;
};

const CommandButtons = ({allowed, globalScope, onOpen}) => {
    const {t} = useTranslation();
    if (!allowed) return null;
    return (
        <div className="settlement-header-actions">
            <button
                className="admin-button admin-button-secondary"
                type="button"
                disabled={globalScope}
                onClick={() => onOpen("report")}
            >
                <FileSpreadsheet aria-hidden="true" size={16} />
                {t("money.settlements.buildReport")}
            </button>
            <button
                className="admin-button admin-button-primary"
                type="button"
                disabled={globalScope}
                onClick={() => onOpen("draft")}
            >
                <FilePlus2 aria-hidden="true" size={16} />
                {t("money.settlements.createDraft")}
            </button>
        </div>
    );
};

const ReportResult = ({result}) => {
    const {t} = useTranslation();
    if (!result) return null;
    return (
        <section className="settlement-report-result" aria-labelledby="settlement-report-title">
            <header>
                <div>
                    <p className="admin-eyebrow">{t("money.settlements.report.eyebrow")}</p>
                    <h2 id="settlement-report-title">{t("money.settlements.report.title")}</h2>
                </div>
                <StatusBadge tone={result.summary.not_ready_count ? "warning" : "success"}>
                    {t("money.settlements.report.ready", {
                        ready: result.summary.ready_count,
                        total: result.summary.total_count,
                    })}
                </StatusBadge>
            </header>
            <div className="settlement-report-metrics">
                <span>
                    <small>{t("money.settlements.report.readyAmount")}</small>
                    <strong>{formatExactMoney(result.summary.ready_payable_amount)}</strong>
                </span>
                <span>
                    <small>{t("money.settlements.report.totalAmount")}</small>
                    <strong>{formatExactMoney(result.summary.total_payable_amount)}</strong>
                </span>
                <span>
                    <small>{t("money.settlements.report.notReady")}</small>
                    <strong>{formatNumber(result.summary.not_ready_count)}</strong>
                </span>
            </div>
            {result.statements.length ? (
                <div className="settlement-result-links">
                    {result.statements.map((statement) => (
                        <Link key={statement.id} to={`/settlements/${statement.id}`}>
                            #{statement.id} {statement.company_name}
                            <MoveRight aria-hidden="true" size={14} />
                        </Link>
                    ))}
                </div>
            ) : null}
        </section>
    );
};

export const SettlementsPage = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const {hasAction} = capabilities;
    const [countryScope, setCountryScope] = useState(() =>
        resolveAdminCountryScope({
            role: capabilities.user?.role,
            allowedCountryIds: capabilities.scope?.country_ids || [],
            storedScope:
                localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
        }),
    );
    const activeCountryScopeRef = useRef(countryScope);
    const [draftFilters, setDraftFilters] = useState(freshFilters);
    const [filters, setFilters] = useState(freshFilters);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_SETTLEMENT_PAGE_SIZE);
    const [filterError, setFilterError] = useState("");
    const [command, setCommand] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [reportResult, setReportResult] = useState(null);
    const [draftResult, setDraftResult] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);

    const closeCommand = useCallback(() => {
        setCommand(null);
        setSelectedCompanyId("");
        setMutationResult(null);
    }, []);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId;
            const normalizedScope = nextScope == null ? null : String(nextScope);
            activeCountryScopeRef.current = normalizedScope;
            setCountryScope(normalizedScope);
            setDraftFilters(freshFilters());
            setFilters(freshFilters());
            setPage(1);
            setPageSize(DEFAULT_SETTLEMENT_PAGE_SIZE);
            setFilterError("");
            setCommand(null);
            setSelectedCompanyId("");
            setReportResult(null);
            setDraftResult(null);
            setMutationResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () =>
            window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const settlementsQuery = useQuery({
        queryKey: settlementKeys.list(countryScope, filters, page, pageSize),
        queryFn: () => listSettlements({countryScope, filters, page, pageSize}),
        enabled: countryScope !== null,
        retry: false,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey?.[1] === String(countryScope) ? previousData : undefined,
    });

    const companiesQuery = useQuery({
        queryKey: settlementKeys.companies(countryScope),
        queryFn: () => listSettlementCompanies({countryScope}),
        enabled: Boolean(command) && countryScope !== null && countryScope !== "0",
        retry: false,
    });

    const branchesQuery = useQuery({
        queryKey: settlementKeys.companyBranches(countryScope, selectedCompanyId),
        queryFn: () =>
            listSettlementCompanyBranches({
                countryScope,
                companyId: selectedCompanyId,
            }),
        enabled:
            Boolean(command) &&
            countryScope !== null &&
            countryScope !== "0" &&
            Boolean(selectedCompanyId),
        retry: false,
    });

    const commandMutation = useMutation({
        mutationFn: ({kind, values, requestScope}) => {
            if (kind === "report") {
                return buildSettlementReport({
                    countryScope: requestScope,
                    periodStart: values.period_start,
                    periodEnd: values.period_end,
                    companyId: values.company_id,
                    branchId: values.branch_id,
                });
            }
            return createSettlementDraft({
                companyId: values.company_id,
                branchId: values.branch_id,
                periodStart: values.period_start,
                periodEnd: values.period_end,
            });
        },
        onSuccess: (result, variables) => {
            void queryClient.invalidateQueries({
                queryKey: settlementKeys.all(variables.requestScope),
            });
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            if (variables.kind === "report") setReportResult(result);
            else setDraftResult(result);
            setMutationResult({kind: "success", message: t("money.settlements.received")});
            setCommand(null);
            setSelectedCompanyId("");
        },
        onError: (error, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            const mapped = mapFinanceError(error);
            setMutationResult(mapped);
        },
    });

    const rows = settlementsQuery.data?.results || [];
    const currency = pageCurrency(rows);
    const summary = {
        payable: currency
            ? addMoney(rows.flatMap((item) => [item.payable_amount, item.adjustment_amount]))
            : null,
        commission: currency ? addMoney(rows.map((item) => item.commission_amount)) : null,
        paid: rows.filter((item) => item.status === "paid").length,
    };

    const submitFilters = (event) => {
        event.preventDefault();
        if (
            draftFilters.period_start &&
            draftFilters.period_end &&
            draftFilters.period_start > draftFilters.period_end
        ) {
            setFilterError(t("money.settlements.dateOrder"));
            return;
        }
        setFilterError("");
        setFilters({...draftFilters});
        setPage(1);
    };

    const resetFilters = () => {
        setDraftFilters(freshFilters());
        setFilters(freshFilters());
        setFilterError("");
        setPage(1);
    };

    const setFilter = (key, value) =>
        setDraftFilters((current) => ({...current, [key]: value}));

    const changePageSize = (nextPageSize) => {
        setPageSize(nextPageSize);
        setPage(1);
    };

    const statusText = (code) => {
        const presentation = getSettlementStatusPresentation(code);
        return t(`money.settlements.statuses.${presentation.known ? code : "unknown"}`);
    };
    const readinessText = (code) => {
        const presentation = getReadinessPresentation(code);
        return t(`money.settlements.readinessStatuses.${presentation.known ? code : "unknown"}`);
    };

    const columns = [
        {
            key: "company",
            label: t("money.settlements.company"),
            render: (item) => (
                <div>
                    <span className="admin-strong">{item.company_name || `#${item.company_id}`}</span>
                    <div className="admin-muted">{t("money.settlements.bin", {bin: item.company_bin_number || "—"})}</div>
                </div>
            ),
        },
        {
            key: "branch",
            label: t("money.settlements.branch"),
            render: (item) =>
                item.branch_name || (item.settlement_scope === "company" ? t("money.settlements.allBranches") : "—"),
        },
        {
            key: "period",
            label: t("money.settlements.period"),
            render: (item) => `${formatDateTime(item.period_start)} — ${formatDateTime(item.period_end)}`,
        },
        {
            key: "status",
            label: t("money.settlements.status"),
            render: (item) => {
                const status = getSettlementStatusPresentation(item.status);
                return <StatusBadge tone={status.tone}>{statusText(item.status)}</StatusBadge>;
            },
        },
        {
            key: "readiness",
            label: t("money.settlements.readiness"),
            render: (item) => {
                const readiness = getReadinessPresentation(item.readiness_status);
                return <StatusBadge tone={readiness.tone}>{readinessText(item.readiness_status)}</StatusBadge>;
            },
        },
        {
            key: "payable_amount",
            label: t("money.settlements.payable"),
            render: (item) => (
                <span className="admin-strong is-money">
                    {formatExactMoney(item.payable_amount, currencyCode(item))}
                </span>
            ),
        },
        {
            key: "action",
            label: "",
            render: (item) => (
                <Link
                    className="settlement-row-link"
                    to={`/settlements/${item.id}`}
                    aria-label={t("money.settlements.openAria", {id: item.id})}
                    title={t("money.settlements.open")}
                >
                    <MoveRight aria-hidden="true" size={17} />
                </Link>
            ),
        },
    ];

    const canGenerate = hasAction("settlements.generate");
    const canExport = hasAction("settlements.export");
    const globalScope = countryScope === "0";
    const dialogFields = command
        ? buildCommandFields({
              kind: command,
              companies: companiesQuery.data || [],
              branches: selectedCompanyId ? branchesQuery.data || [] : [],
              t,
          })
        : [];
    const companiesLookupNote = !command
        ? ""
        : companiesQuery.isLoading
          ? t("money.settlements.companiesLoading")
          : companiesQuery.isError
            ? t("money.settlements.companiesError")
            : (companiesQuery.data || []).length === 0
              ? t("money.settlements.companiesEmpty")
              : "";

    return (
        <div className="admin-page settlement-page">
            <header className="admin-page-header settlement-page-header">
                <div>
                    <h1 className="admin-page-title">{t("money.settlements.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.settlements.copy")}
                    </p>
                </div>
                <CommandButtons
                    allowed={canGenerate}
                    globalScope={globalScope}
                    onOpen={setCommand}
                />
                {canExport ? (
                    <Link className="admin-button admin-button-secondary" to="/settlements/exports">
                        <History aria-hidden="true" size={16} /> {t("money.settlements.exportsLink")}
                    </Link>
                ) : null}
            </header>

            {canGenerate && globalScope ? (
                <p className="settlement-scope-warning">{t("money.settlements.scopeWarning")}</p>
            ) : null}

            <section className="admin-grid cols-4" aria-label={t("money.settlements.metricsAria")}>
                <article className="admin-card">
                    <p className="admin-card-title"><Calculator size={14} /> {t("money.settlements.statements")}</p>
                    <p className="admin-card-value">{formatNumber(rows.length)}</p>
                    <p className="admin-card-note">{t("money.settlements.countNote")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("money.settlements.payable")}</p>
                    <p className="admin-card-value is-money">
                        {summary.payable === null ? "—" : formatExactMoney(summary.payable, currency)}
                    </p>
                    <p className="admin-card-note">
                        {currency
                            ? t("money.settlements.payableNote")
                            : t("money.settlements.mixedCurrency")}
                    </p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><Landmark size={14} /> {t("money.settlements.commission")}</p>
                    <p className="admin-card-value">
                        {summary.commission === null
                            ? "—"
                            : formatExactMoney(summary.commission, currency)}
                    </p>
                    <p className="admin-card-note">
                        {currency
                            ? t("money.settlements.commissionNote")
                            : t("money.settlements.mixedCurrency")}
                    </p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("money.settlements.paid")}</p>
                    <p className="admin-card-value">{formatNumber(summary.paid)}</p>
                    <p className="admin-card-note">{t("money.settlements.paidNote")}</p>
                </article>
            </section>

            <form className="admin-toolbar settlement-filter-form" onSubmit={submitFilters}>
                <label className="admin-field">
                    <span>{t("money.settlements.status")}</span>
                    <select value={draftFilters.status} onChange={(event) => setFilter("status", event.target.value)}>
                        <option value="">{t("money.settlements.all")}</option>
                        {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{statusText(status)}</option>
                        ))}
                    </select>
                </label>
                <label className="admin-field">
                    <span>{t("money.settlements.readiness")}</span>
                    <select value={draftFilters.readiness_status} onChange={(event) => setFilter("readiness_status", event.target.value)}>
                        <option value="">{t("money.settlements.all")}</option>
                        {READINESS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{readinessText(status)}</option>
                        ))}
                    </select>
                </label>
                <label className="admin-field">
                    <span>{t("money.settlements.companyId")}</span>
                    <input inputMode="numeric" value={draftFilters.company_id} onChange={(event) => setFilter("company_id", event.target.value)} />
                </label>
                <label className="admin-field">
                    <span>{t("money.settlements.branchId")}</span>
                    <input inputMode="numeric" value={draftFilters.branch_id} onChange={(event) => setFilter("branch_id", event.target.value)} />
                </label>
                <label className="admin-field">
                    <span>{t("money.settlements.periodFrom")}</span>
                    <input type="date" value={draftFilters.period_start} onChange={(event) => setFilter("period_start", event.target.value)} />
                </label>
                <label className="admin-field">
                    <span>{t("money.settlements.periodTo")}</span>
                    <input type="date" value={draftFilters.period_end} onChange={(event) => setFilter("period_end", event.target.value)} />
                </label>
                <button className="admin-button admin-button-primary" type="submit">
                    <Search aria-hidden="true" size={15} /> {t("money.settlements.apply")}
                </button>
                <button className="admin-button admin-button-secondary" type="button" onClick={resetFilters}>
                    <RotateCcw aria-hidden="true" size={15} /> {t("money.settlements.reset")}
                </button>
                <PageSizeSelect
                    value={pageSize}
                    options={PAGE_SIZE_OPTIONS}
                    onChange={changePageSize}
                />
                {filterError ? <p className="settlement-filter-error" role="alert">{filterError}</p> : null}
            </form>

            <MutationResultBanner result={mutationResult} />
            <ReportResult result={reportResult} />
            {draftResult?.statements?.length ? (
                <section className="settlement-draft-result" aria-label={t("money.settlements.draftsAria")}>
                    <strong>{t("money.settlements.draftsTitle")}</strong>
                    {draftResult.statements.map((statement) => (
                        <Link key={statement.id} to={`/settlements/${statement.id}`}>
                            #{statement.id} {statement.company_name}
                        </Link>
                    ))}
                </section>
            ) : null}

            {settlementsQuery.isError ? (
                <DataState
                    title={t("money.settlements.loadError")}
                    copy={mapFinanceError(settlementsQuery.error).message}
                />
            ) : (
                <AdminTable
                    columns={columns}
                    rows={rows}
                    loading={settlementsQuery.isLoading}
                    emptyText={t("money.settlements.empty")}
                />
            )}

            {settlementsQuery.data ? (
                <ServerPagination
                    count={settlementsQuery.data.count}
                    page={page}
                    pageSize={pageSize}
                    previous={settlementsQuery.data.previous}
                    next={settlementsQuery.data.next}
                    pending={settlementsQuery.isFetching}
                    onPageChange={setPage}
                />
            ) : null}

            <FinancialCommandDialog
                open={Boolean(command)}
                dialogKey={command}
                title={command === "draft" ? t("money.settlements.createDraft") : t("money.settlements.buildReport")}
                description={
                    companiesLookupNote
                        ? t("money.settlements.commandDescriptionWithNote", {note: companiesLookupNote})
                        : t("money.settlements.commandDescription")
                }
                entity={t("money.settlements.countryEntity", {id: countryScope})}
                fields={dialogFields}
                pending={commandMutation.isPending}
                error={mutationResult?.kind && mutationResult.kind !== "success" ? mutationResult : null}
                confirmLabel={command === "draft" ? t("money.settlements.create") : t("money.settlements.build")}
                onCancel={closeCommand}
                onFieldChange={(name, value) => {
                    if (name === "company_id") {
                        setSelectedCompanyId(value);
                    }
                }}
                onConfirm={(values) =>
                    commandMutation.mutate({kind: command, values, requestScope: countryScope})
                }
            />
        </div>
    );
};
